import type { ISODate } from '../store/types'

const DAY_MS = 86_400_000

/* ------------------------------------------------------------------ locale */

/**
 * Date helpers are called from selectors and other plain functions, not just
 * components, so the locale and the handful of words they need are pushed in
 * once by `<I18nProvider>` rather than threaded through every call site.
 */
interface DateWords {
  today: string
  tomorrow: string
  yesterday: string
  inDays: (n: number) => string
  daysAgo: (n: number) => string
  overdue: string
  dueToday: string
  days: (n: number) => string
  weeks: (n: number) => string
  hours: (n: number) => string
  minutes: (n: number) => string
  hoursMinutes: (h: number, m: number) => string
  morning: string
  afternoon: string
  evening: string
}

let LOCALE = 'en-AU'
let W: DateWords = {
  today: 'today',
  tomorrow: 'tomorrow',
  yesterday: 'yesterday',
  inDays: (n) => `in ${n} days`,
  daysAgo: (n) => `${n} days ago`,
  overdue: 'overdue',
  dueToday: 'due today',
  days: (n) => `${n} d`,
  weeks: (n) => `${n} wks`,
  hours: (n) => `${n}h`,
  minutes: (n) => `${n}m`,
  hoursMinutes: (h, m) => `${h}h ${m}m`,
  morning: 'Good morning',
  afternoon: 'Good afternoon',
  evening: 'Good evening',
}

export function configureDates(locale: string, words: DateWords): void {
  LOCALE = locale
  W = words
}

/** Locale digits — Persian numerals under `fa`, Latin under `en`. */
export function num(n: number): string {
  return new Intl.NumberFormat(LOCALE, { useGrouping: false }).format(n)
}

/* ------------------------------------------------------------------- core */

/** Local-timezone `YYYY-MM-DD`. Deliberately not `toISOString()`, which is UTC
 *  and rolls the date over for anyone east of Greenwich after ~10am. */
export function toISO(d: Date): ISODate {
  const m = `${d.getMonth() + 1}`.padStart(2, '0')
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** Parses `YYYY-MM-DD` as local midnight. */
export function fromISO(s: ISODate): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, (m ?? 1) - 1, d ?? 1)
}

export const today = (): ISODate => toISO(new Date())

export function addDays(s: ISODate, n: number): ISODate {
  const d = fromISO(s)
  d.setDate(d.getDate() + n)
  return toISO(d)
}

/** Whole days from `a` to `b`; negative when `b` is in the past. */
export function daysBetween(a: ISODate, b: ISODate): number {
  return Math.round((fromISO(b).getTime() - fromISO(a).getTime()) / DAY_MS)
}

/** Monday-start week index (0 = Mon … 6 = Sun). */
export function weekday(s: ISODate): number {
  return (fromISO(s).getDay() + 6) % 7
}

export function startOfWeek(s: ISODate): ISODate {
  return addDays(s, -weekday(s))
}

/** The 6×7 grid of dates covering the month that contains `anchor`. */
export function monthGrid(anchor: ISODate): ISODate[] {
  const d = fromISO(anchor)
  const first = toISO(new Date(d.getFullYear(), d.getMonth(), 1))
  const start = startOfWeek(first)
  return Array.from({ length: 42 }, (_, i) => addDays(start, i))
}

export function monthOf(s: ISODate): number {
  return fromISO(s).getMonth()
}

export function shiftMonth(s: ISODate, n: number): ISODate {
  const d = fromISO(s)
  // Clamp to the 1st first, so shifting from the 31st never skips a month.
  return toISO(new Date(d.getFullYear(), d.getMonth() + n, 1))
}

/* --------------------------------------------------------------- formatting */

/** Localised short weekday names, Monday first. */
export function weekdayNames(style: 'short' | 'narrow' = 'short'): string[] {
  const fmt = new Intl.DateTimeFormat(LOCALE, { weekday: style })
  // 2024-01-01 was a Monday.
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i)))
}

export function fmtDate(s: ISODate, opts: Intl.DateTimeFormatOptions = {}): string {
  return fromISO(s).toLocaleDateString(LOCALE, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...opts,
  })
}

export function fmtMonthYear(s: ISODate): string {
  return fromISO(s).toLocaleDateString(LOCALE, { month: 'long', year: 'numeric' })
}

/** "today" / "tomorrow" / "in 4 days" / "3 days ago" / a date past a fortnight. */
export function relDay(s: ISODate, from: ISODate = today()): string {
  const n = daysBetween(from, s)
  if (n === 0) return W.today
  if (n === 1) return W.tomorrow
  if (n === -1) return W.yesterday
  if (n > 1 && n <= 14) return W.inDays(n)
  if (n < -1 && n >= -14) return W.daysAgo(-n)
  return fmtDate(s, { weekday: undefined })
}

/** Compact countdown used on deadline tiles. */
export function countdown(s: ISODate, from: ISODate = today()): string {
  const n = daysBetween(from, s)
  if (n < 0) return W.overdue
  if (n === 0) return W.dueToday
  if (n < 14) return W.days(n)
  return W.weeks(Math.round(n / 7))
}

export function isOverdue(s: ISODate, from: ISODate = today()): boolean {
  return daysBetween(from, s) < 0
}

export function fmtMins(mins: number): string {
  const h = Math.floor(mins / 60)
  const m = Math.round(mins % 60)
  if (!h) return W.minutes(m)
  return m ? W.hoursMinutes(h, m) : W.hours(h)
}

export function fmtClock(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec))
  const mm = Math.floor(s / 60)
  const ss = s % 60
  return `${num(mm)}:${ss < 10 ? num(0) + num(ss) : num(ss)}`
}

export function greeting(d: Date = new Date()): string {
  const h = d.getHours()
  if (h < 12) return W.morning
  if (h < 18) return W.afternoon
  return W.evening
}
